import Skeleton from "react-loading-skeleton";
import { useCart } from "~/hooks/cart";
import { type DateLike, useStoreTime } from "~/hooks/hours";
import { useDispatchTypeLabel } from "~/hooks/language";
import { ChevronDownIcon } from "~/icons";
import type { Location, Page, TimeSlot } from "~/types";
import { Box } from "../Box/Box";
import { Text } from "../Text/Text";
import styles from "./OrderingOptionsButton.module.css";

export function OrderingOptionsButton(props: {
  location: Location;
  page: Page | null;
  onClickDetails: () => void;
}): React.ReactNode {
  const {
    dispatchOption,
    timeSlot: selectedTimeslot,
    // Could use the label from here if we fallback to PICK_UP inside
    // dispatchOptionLabel,
  } = useCart(props.location.ref);
  const { formatTime, formatDate, formatZone } = useStoreTime();

  // NOTE: Defaulting the label to Pick Up if there is no dispatch option selected, or none available
  // This is the happy path so the screen doesn't show an irrelevant loading state.
  // TODO: We should prompt user if none selected, right?
  const { label: dispatchLabel } = useDispatchTypeLabel(
    dispatchOption || "PICK_UP",
  );

  return (
    <Box
      onClick={props.onClickDetails}
      flexDirection="row"
      display="flex"
      alignItems="center"
      border="standard"
      borderColor="neutral-700"
      paddingY="2"
      paddingX="1"
      height="touchable"
      minWidth={0}
      className={styles.optionsButton}
    >
      <Box display={"flex"} flexDirection={"column"} minWidth={0}>
        <Box display={"flex"} flexDirection={"row"} minWidth={0}>
          <Text
            size="xsmall"
            weight="strong"
            color="white"
            transform="uppercase"
            whiteSpace="nowrap"
            className={styles.condensed}
          >
            {dispatchLabel || <Skeleton width={75} />}
          </Text>
          <Box paddingLeft="1" minWidth={0}>
            <Text
              size="xsmall"
              weight="strong"
              color="white"
              truncate={true}
              priority="low"
              className={styles.condensed}
            >
              {selectedTimeslot
                ? collectionTimeSlotsSelectedDateFormat(
                    selectedTimeslot,
                    formatTime,
                    formatDate,
                    formatZone,
                  )
                : "ASAP"}
            </Text>
          </Box>
        </Box>
        <Box
          display={"flex"}
          flexDirection={"row"}
          minWidth={0}
          className={styles.locationName}
        >
          <Text
            size="small"
            weight="strong"
            color="white"
            truncate={true}
            priority="low"
            transform="uppercase"
            className={styles.condensed}
          >
            {props.location.name || <Skeleton width={150} />}
          </Text>
        </Box>
      </Box>
      <Box
        className={styles.caretContainer}
        height="full"
        flexShrink={0}
        flexDirection={"column"}
      >
        <ChevronDownIcon className={styles.caret} />
      </Box>
    </Box>
  );
}

function collectionTimeSlotsSelectedDateFormat(
  timeSlot: TimeSlot,
  formatTime: (date: DateLike) => string,
  formatDate: (date: DateLike, pattern?: string) => string,
  formatZone: (date: DateLike) => string,
): string {
  const startDate = formatDate(timeSlot.startTime, "ccc, d MMM");
  const startTime = formatTime(timeSlot.startTime);

  return `${startDate}, ${startTime} ${formatZone(timeSlot.startTime)}`;
}
